// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartPropsGridSchema.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import SCHEMA_DEF from 'logic/data/Izenda.ReportPartPropsGridSchema';
import constants from 'logic/data/Izenda.ReportPartGridConstants';
import PropertiesEditorManager from 'modules/Izenda.PropertiesEditorManager';
import { action } from 'mobx';
import Panel from 'share/components/controls/Izenda.Panel';
import SizeSetting from 'share/components/controls/Izenda.SizeSetting';
import FontStyle from 'share/components/controls/Izenda.FontStyle';
import FontFormat from 'share/components/controls/Izenda.FontFormat';
import ColorStyling from 'share/components/controls/Izenda.ColorStyling';
import AlignmentSetting from 'share/components/controls/Izenda.AlignmentSetting';
import VerticalAlignmentSetting from 'share/components/controls/Izenda.VerticalAlignmentSetting';
import ButtonHighlight from 'share/components/controls/Izenda.ButtonHighlight';
import CustomDropdown from 'share/components/controls/Izenda.CustomDropdown';
import { isEmpty, get, cloneDeep, isEqual, values, getOr, filter } from 'lodash/fp';

const WIDGETS = {
  Panel,
  SizeSetting,
  FontStyle,
  FontFormat,
  ColorStyling,
  AlignmentSetting,
  VerticalAlignmentSetting,
  ButtonHighlight,
  CustomDropdown
};

export default function(reportDesignerModel, reportPart, fieldStore, props) {
  const reportPartContent = reportPart.reportPartContent;
  const configReportPartProperties = reportPartContent.properties;

  ensure(!!reportDesignerModel, 'reportDesignerModel must be set');

  return {
    // define data to render UI
    definedSchema: SCHEMA_DEF,

    // list of widgets which are used to render UI
    widgets: WIDGETS,

    // data to binding to fields
    source: configReportPartProperties,

    // properties for different fields
    mappingProps: {
      beforeChangeSeparator: function(newVal) {
        if (!!newVal.value || isEmpty(get('separators.elements', reportPartContent))) {
          return Promise.resolve();
        }

        return props.confirmFn('Confirmation', {
          description: constants.MESSAGES.SEPARATOR_CONFIRM
        });
      },
      fnResetRefreshIntervalDefaultValue: configReportPartProperties.resetRefreshInterval.bind(
        configReportPartProperties
      ),
      fnResetConditionalFormattingDefaultValue: configReportPartProperties.resetConditionalFormatting.bind(
        configReportPartProperties
      ),
      fnResetBorderDefaultValue: configReportPartProperties.resetBorderDefaultValue.bind(configReportPartProperties),
      getBorderSettings: function() {
        return configReportPartProperties.getTableByKey('border');
      },
      hadSettingBorder: function() {
        const borderSettings = configReportPartProperties.getTableByKey('border');
        const defaultSetting = cloneDeep(constants.DEFAULT_SETTING_VALUE.border);

        if (!borderSettings) {
          return false;
        }

        return !isEqual(borderSettings, defaultSetting);
      },
      getDataRefreshInterval: function() {
        return configReportPartProperties.getViewByKey('dataRefreshInterval');
      },
      hadSettingRefreshInterval: function() {
        const defaultSetting = constants.DEFAULT_SETTING_VALUE.refreshIntervalDefault;
        const refreshSetting = configReportPartProperties.getViewByKey('dataRefreshInterval');

        if (!refreshSetting) {
          return false;
        }

        return (
          refreshSetting.value !== defaultSetting.value ||
          refreshSetting.updateInterval !== defaultSetting.updateInterval
        );
      },
      getConditionalFormatting: function() {
        return configReportPartProperties.getViewByKey('conditionalFormatting');
      },
      getConditionalFormattingWithReportPart: function() {
        const conditionalFormatting = configReportPartProperties.getViewByKey('conditionalFormatting');

        return {
          reportPartContent,
          conditionalFormatting 
        };
      },
      hadConditionalFormattingSetting: function(){
        const conditionalFormatting = configReportPartProperties.getViewByKey('conditionalFormatting');
        return conditionalFormatting.rules && conditionalFormatting.rules.length !== 0;
      },
      getWordWrapValue: function() {
        return configReportPartProperties.headers;
      },
      getColumnsWordWrapValue: function() {
        return configReportPartProperties.columns;
      },
      getColumnAlignment: function() {
        return configReportPartProperties.columns.alignment;
      },
      getColumnVerticalAlignment: function() {
        return configReportPartProperties.columns.verticalAlignment;
      },
      filterFreezeHeaderOptions: function(options) {
        const {
          GRID_STYLES,
          FREEZE_HEADERS: { VERTICALLY, HORIZONTALLY }
        } = constants;
        switch (reportPartContent.gridStyle) {
          case GRID_STYLES.VERTICAL.value:
            return filter(item => item.value === VERTICALLY.value, options);
          case GRID_STYLES.HORIZONTAL.value:
            return filter(item => item.value === HORIZONTALLY.value, options);
          case GRID_STYLES.DRILL_DOWN.value:
            return filter(item => item.value === VERTICALLY.value, options);
          default:
            return options;
        }
      }
    },

    // defined-function which fields need to execute to get its data
    mappingSource: {
      getWidthValue: function() {
        return configReportPartProperties.columns;
      },
      getGridStyle: function() {
        return values(constants.GRID_STYLES);
      },
      getSeparatorStyle: function() {
        return values(constants.SEPARATOR_STYLE);
      },
      getHeaderFontFormat: function() {
        const fontConfig = configReportPartProperties.getHeadersByKey('font');

        return {
          fontFamily: fontConfig.family,
          fontSize: fontConfig.size
        };
      },
      getHeaderFontStyle: function() {
        const fontConfig = configReportPartProperties.getHeadersByKey('font');

        return {
          fontUnderline: fontConfig.underline,
          fontItalic: fontConfig.italic,
          fontBold: fontConfig.bold
        };
      },
      getHeaderColorStyling: function() {
        const fontConfig = configReportPartProperties.getHeadersByKey('font');

        return {
          textColor: fontConfig.color,
          backgroundColor: fontConfig.backgroundColor
        };
      },
      getTableColorStyling: function() {
        return {
          backgroundColor: configReportPartProperties.getTableByKey('backgroundColor')
        };
      },
      getTableAlternativeBackgroundValue: function() {
        const isAlternativeColumns = configReportPartProperties.getColumnsByKey('alterBackgroundColor');
        const isAlternativeRows = configReportPartProperties.getRowsByKey('alterBackgroundColor');

        // Populate 2 flags rows, columns into table alternative background
        if (isAlternativeColumns && isAlternativeRows) {
          return constants.ALTERNATIVE_BACKGROUND.COLUMNS_AND_ROWS.value;
        }

        if (isAlternativeColumns) {
          return constants.ALTERNATIVE_BACKGROUND.COLUMNS.value;
        }

        if (isAlternativeRows) {
          return constants.ALTERNATIVE_BACKGROUND.ROWS.value;
        }

        return constants.ALTERNATIVE_BACKGROUND.NONE.value;
      },
      getTableAlternativeBackgroundOptions: function() {
        return values(constants.ALTERNATIVE_BACKGROUND);
      },
      isHiddenByAlternativeBackground: function() {
        const isAlternativeColumns = configReportPartProperties.getColumnsByKey('alterBackgroundColor');
        const isAlternativeRows = configReportPartProperties.getRowsByKey('alterBackgroundColor');

        return !isAlternativeColumns && !isAlternativeRows;
      },
      getTableAlternativeBackgroundColor: function() {
        return {
          backgroundColor: configReportPartProperties.getTableByKey('alternativeBackgroundColor')
        };
      },
      isNotGridDrillDown: function() {
        return reportPartContent.gridStyle !== constants.GRID_STYLES.DRILL_DOWN.value && reportPartContent.gridStyle !== constants.GRID_STYLES.PIVOT.value;
      },
      isExpanderRowsSpacingHidden: function() {
          return reportPartContent.gridStyle !== constants.GRID_STYLES.PIVOT.value;
      },
      isGridDrillDownOrPivot: function() {
        return reportPartContent.gridStyle === constants.GRID_STYLES.DRILL_DOWN.value || reportPartContent.gridStyle === constants.GRID_STYLES.PIVOT.value;
      },
      getFreezeHeaderOptions() {
        return values(constants.FREEZE_HEADERS);
      },
      getFreezeHeaderValue: function() {
        return configReportPartProperties.getHeadersByKey('freezeHeaders');
      }
    },

    onChangeValue: action(function(schemaData, changedKey, changedKeyPath, changedValue, changedOthersInfo) {
      const groupData = changedKeyPath && changedKeyPath.length ? get(changedKeyPath, schemaData) : schemaData;
      const fieldData = groupData[changedKey];

      fieldData && (fieldData.value = changedValue);

      /* general info section*/
      const generalInfo = schemaData.generalInfo;
      const generalInfoGridStyle = generalInfo.gridStyle.value;
      const generalInfoSeparatorStyle = generalInfo.separatorStyle.value;
      /* general info section*/

      /* table section*/
      const table = schemaData.table;

      const tableBackgroundColor = table.backgroundColor.value.backgroundColor;
      const tableAlternativeBackground = table.alternativeBackground.value;
      const tableAlternativeBackgroundColor =
        tableAlternativeBackground === constants.DEFAULT_VALUE_PROPERTIES.TABLE.ALTERNATIVE_BACKGROUND
          ? constants.DEFAULT_VALUE_PROPERTIES.TABLE.ALTERNATIVE_BACKGROUND_COLOR
          : table.alternativeBackgroundColor.value.backgroundColor;
      const borderSettings = getOr({}, 'border.value', table);
      /* table section*/

      /* columns section*/
      const columns = schemaData.columns;
      const columnsWidthValue = columns.width.value;
      const columnsAlternatingBgColor =
        tableAlternativeBackground === constants.ALTERNATIVE_BACKGROUND.COLUMNS.value ||
        tableAlternativeBackground === constants.ALTERNATIVE_BACKGROUND.COLUMNS_AND_ROWS.value;
      const columnsWordWrap = columns.columnWordWrap.value;
      const columnsAlignment = columns.columnAlignment.value;
      const columnsVerticalAlignment = columns.columnVerticalAlignment.value;
      /* columns section*/

      /* rows section*/
      const rowsAlternatingBgColor =
        tableAlternativeBackground === constants.ALTERNATIVE_BACKGROUND.ROWS.value ||
        tableAlternativeBackground === constants.ALTERNATIVE_BACKGROUND.COLUMNS_AND_ROWS.value;
      /* rows section*/

      /* headers section*/
      const headers = schemaData.headers;
      const headersAlignment = headers.alignment.value;
      const headersVerticalAlignment = headers.verticalAlignment.value;

      const headersWordWrap = headers.wordWrap.value;
      const headersRemoveHeaderForExport = headers.removeHeaderForExport.value;
      const headersRemoveHeaderForViewer = headers.removeHeaderForViewer.value;
      const headersFreezeHeaders = headers.freezeHeaders.value;

      const headersFontFormat = headers.fontFormat.value;
      const headersFontFormatFontFamily = headersFontFormat.fontFamily;
      const headersFontFormatFontSize = headersFontFormat.fontSize;

      const headersFontStyle = headers.fontStyle.value;
      const headersFontStyleFontBold = headersFontStyle.fontBold;
      const headersFontStyleFontItalic = headersFontStyle.fontItalic;
      const headersFontStyleFontUnderline = headersFontStyle.fontUnderline;

      const headersColor = headers.color.value;
      const headersColorTextColor = headersColor.textColor;
      const headersColorBackground = headersColor.backgroundColor;
      /* headers section*/

      /* grouping section*/
      const grouping = schemaData.grouping;
      const groupingUseSeparator = grouping.useSeparator.value;
      /* grouping section*/

      /* view section*/
      const view = schemaData.view;
      const dataRefreshInterval = view.dataRefreshInterval.value;
      const conditionalFormatting = view.conditionalFormatting.value;
      const usePagination = view.usePagination.value;
      const expanderRowsSpacing = view.expanderRowsSpacing
        ? view.expanderRowsSpacing.value
        : false;
      const collapseDrilldownByDefault = view.collapseDrilldownByDefault.value;
      const pivotColumnsPerExportedPage = view.pivotColumnsPerExportedPage.value;
      /* end view section*/

      /* printing section*/
      const printing = schemaData.printing;
      const usePageBreakAfterSeparator = printing.usePageBreakAfterSeparator.value;

      const data = {
        generalInfo: {
          gridStyle: generalInfoGridStyle,
          separatorStyle: generalInfoSeparatorStyle
        },
        table: {
          backgroundColor: tableBackgroundColor,
          border: borderSettings,
          alternativeBackgroundColor: tableAlternativeBackgroundColor
        },
        columns: {
          width: {
            value: columnsWidthValue
          },
          alterBackgroundColor: columnsAlternatingBgColor,
          wordWrap: columnsWordWrap,
          alignment: columnsAlignment,
          verticalAlignment: columnsVerticalAlignment
        },
        rows: {
          alterBackgroundColor: rowsAlternatingBgColor
        },
        headers: {
          font: {
            family: headersFontFormatFontFamily,
            size: headersFontFormatFontSize,
            bold: headersFontStyleFontBold,
            italic: headersFontStyleFontItalic,
            underline: headersFontStyleFontUnderline,
            color: headersColorTextColor,
            backgroundColor: headersColorBackground
          },
          alignment: headersAlignment,
          verticalAlignment: headersVerticalAlignment,
          wordWrap: headersWordWrap,
          removeHeaderForExport: headersRemoveHeaderForExport,
          removeHeaderForViewer: headersRemoveHeaderForViewer,
          freezeHeaders: headersFreezeHeaders
        },
        grouping: {
          useSeparator: groupingUseSeparator
        },
        view: {
          dataRefreshInterval: dataRefreshInterval,
          conditionalFormatting: conditionalFormatting,
          usePagination: usePagination,
          expanderRowsSpacing: expanderRowsSpacing,
          collapseDrilldownByDefault: collapseDrilldownByDefault,
          pivotColumnsPerExportedPage: pivotColumnsPerExportedPage
        },
        printing: {
          usePageBreakAfterSeparator: usePageBreakAfterSeparator
        }
      };

      let oldGridStyle = null;
      let needUpdatePreview = false;
      let promiseUpdateFunction = Promise.resolve();

      if (changedKey === 'gridStyle') {
        // Reset value freezeHeaders
        data.headers.freezeHeaders = null;
        headers.freezeHeaders.value = null;
        oldGridStyle = configReportPartProperties.getGeneralInfoByKey('gridStyle');

        if(generalInfoGridStyle === constants.GRID_STYLES.DRILL_DOWN.value 
          || generalInfoGridStyle === constants.GRID_STYLES.PIVOT.value){
            data.view.conditionalFormatting.rules = [];
        }

        promiseUpdateFunction = reportPartContent.changeGridStyle(oldGridStyle, generalInfoGridStyle, reportPart);
        needUpdatePreview = true;
      }

      if (changedKey === 'useSeparator' && !groupingUseSeparator) {
        reportPart.clearContainer(reportPartContent.separators);
        needUpdatePreview = true;
      }

      if (changedKey === 'usePagination') {
        needUpdatePreview = true;
      }

      if (changedKey === 'expanderRowsSpacing') {
        needUpdatePreview = true;
      }

      if (changedKey === 'collapseDrilldownByDefault') {
        needUpdatePreview = true;
      }

      if (changedKey === 'conditionalFormatting') {
        reportPartContent.checkIfPercentOfGroupRequiredForConditionalFormatting(changedValue);
        needUpdatePreview = true;
      }

      configReportPartProperties.setReportPartProperties(data);

      if (needUpdatePreview && reportPart.shouldUpdatePreview) {
        promiseUpdateFunction.then(() => {
          fieldStore.needUpdatePreview(reportPart);
        });
      }
    })
  };
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartGrid/Izenda.ReportPartPropsGridSchema.js